/**
 * Bỏ thẻ HTML trong `movie.content` rồi co khoảng trắng.
 * API trả mô tả dạng "<p>...</p>", đôi khi có cả &amp; và &nbsp;.
 */
export function stripHtml(html: string | undefined | null): string {
	if (!html) return '';
	return html
		.replace(/<br\s*\/?>/gi, ' ')
		.replace(/<\/p>/gi, ' ')
		.replace(/<[^>]*>/g, '')
		.replace(/&nbsp;/gi, ' ')
		.replace(/&amp;/gi, '&')
		.replace(/&quot;/gi, '"')
		.replace(/&#39;/gi, "'")
		.replace(/&lt;/gi, '<')
		.replace(/&gt;/gi, '>')
		.replace(/\s+/g, ' ')
		.trim();
}

/** Điểm đánh giá: API trả 0 khi CHƯA có điểm, nên 0 phải hiểu là "không có". */
export function parseScore(
	...values: Array<number | string | null | undefined>
): number | null {
	for (const value of values) {
		const score = typeof value === 'string' ? Number(value) : value;
		if (typeof score === 'number' && Number.isFinite(score) && score > 0) {
			return Math.round(score * 10) / 10;
		}
	}
	return null;
}

export function formatScore(score: number | null): string {
	return score === null ? 'N/A' : score.toFixed(1);
}

export function formatViews(views: number): string {
	if (!Number.isFinite(views) || views <= 0) return '—';
	if (views >= 1_000_000) return `${(views / 1_000_000).toFixed(1)}M`;
	if (views >= 1_000) return `${(views / 1_000).toFixed(1)}K`;
	return String(views);
}

export function parseEpisodeTotal(
	value: number | string | undefined,
): number | null {
	if (typeof value === 'number') return value > 0 ? value : null;
	if (typeof value === 'string') {
		const parsed = Number.parseInt(value, 10);
		return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
	}
	return null;
}

const RELEASE_STATUS_LABELS: Record<string, string> = {
	ongoing: 'Đang phát sóng',
	completed: 'Đã hoàn thành',
	trailer: 'Sắp ra mắt',
	canceled: 'Đã huỷ',
};

export function formatReleaseStatus(status: string): string {
	return RELEASE_STATUS_LABELS[status] ?? 'Đang cập nhật';
}

/** "2026-09-17T14:01:19.000Z" → "2 giờ trước". */
export function formatRelativeTime(iso: string | null): string {
	if (!iso) return '';
	const timestamp = Date.parse(iso);
	if (Number.isNaN(timestamp)) return '';

	const diffSeconds = Math.round((timestamp - Date.now()) / 1000);
	const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
		['year', 31_536_000],
		['month', 2_592_000],
		['day', 86_400],
		['hour', 3_600],
		['minute', 60],
	];

	const formatter = new Intl.RelativeTimeFormat('vi', { numeric: 'auto' });
	for (const [unit, seconds] of units) {
		if (Math.abs(diffSeconds) >= seconds) {
			return formatter.format(Math.round(diffSeconds / seconds), unit);
		}
	}
	return 'vừa xong';
}

/** Ghép danh sách kiểu "A, B và C"; API hay trả ["Đang cập nhật"] khi trống. */
export function formatPeople(people: string[]): string {
	const cleaned = people
		.map((person) => person.trim())
		.filter((person) => person && person.toLowerCase() !== 'đang cập nhật');
	return cleaned.length ? cleaned.join(', ') : 'Đang cập nhật';
}

/** "Tập 01" → "01"; dùng để hiển thị số tập gọn trong grid chọn tập. */
export function shortEpisodeLabel(name: string): string {
	const match = name.match(/(\d+(?:[.\-–]\d+)?)/);
	if (match?.[1]) return match[1];
	return name.replace(/^tập\s*/i, '').trim() || name;
}
